/* The API client. Every call goes to our own origin with the session cookies (credentials: 'same-origin'); every
   non-GET request carries the x-pa: 1 header the server uses against cross-site requests. The web app never sees
   tokens: the server keeps the session in httpOnly cookies. */
import { CSRF_HEADER, WEB_ROUTES, type ErrorCode } from '@pathabroad/core/browser';
import { codeUrl, signinUrl } from './routes';
import type { MessageKey } from '../i18n/en';

export type ClientErrorCode = ErrorCode | 'network';
export type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export class ApiError extends Error {
  readonly status: number;
  readonly code: ClientErrorCode;
  readonly serverMessage?: string;
  readonly field?: string;
  readonly body?: unknown;
  constructor(status: number, code: ClientErrorCode, serverMessage?: string, field?: string, body?: unknown) {
    super(`${status || 'network'} ${code}`);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.serverMessage = serverMessage;
    this.field = field;
    this.body = body;
  }
}

const KNOWN: ReadonlySet<string> = new Set<ErrorCode>(['signed_out', 'mfa_required', 'bad_credentials', 'email_not_confirmed', 'bad_code',
  'age_blocked', 'guardian_consent_required', 'plan_required', 'already_paid', 'withdrawal_ack_required', 'version_conflict',
  'invalid', 'not_found', 'rate_limited', 'bad_signature', 'forbidden', 'server_error']);

/* when the body isn't a contract error, fall back on the HTTP status */
export function codeForStatus(status: number): ErrorCode {
  if (status === 401) return 'signed_out';
  if (status === 402) return 'plan_required';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'not_found';
  if (status === 409) return 'version_conflict';
  if (status === 429) return 'rate_limited';
  if (status >= 400 && status < 500) return 'invalid';
  return 'server_error';
}

export type FetchLike = (input: string, init: RequestInit) => Promise<Response>;
let fetchImpl: FetchLike = (input, init) => fetch(input, init);
/* tests swap in a fake fetch */
export function setFetch(f: FetchLike) { fetchImpl = f; }

export async function request<T>(method: Method, path: string, body?: unknown, opts: { signal?: AbortSignal } = {}): Promise<T> {
  const headers: Record<string, string> = { accept: 'application/json' };
  const init: RequestInit = { method, credentials: 'same-origin', headers, signal: opts.signal, cache: 'no-store' };
  if (method !== 'GET') {
    headers[CSRF_HEADER] = '1';
    headers['content-type'] = 'application/json';
    init.body = JSON.stringify(body ?? {});
  }
  let res: Response;
  try {
    res = await fetchImpl(path, init);
  } catch (e) {
    if ((e as Error)?.name === 'AbortError') throw e;
    throw new ApiError(0, 'network');
  }
  if (!res.ok) throw await toError(res);
  const text = res.status === 204 ? '' : await res.text().catch(() => '');
  let data: unknown;
  if (text) { try { data = JSON.parse(text); } catch { data = undefined; } }
  return data as T;
}

/* a contract error body ({ error, message, field }) or, failing that, the HTTP status */
async function toError(res: Response): Promise<ApiError> {
  let data: unknown;
  try { data = JSON.parse(await res.text()); } catch { data = undefined; }
  const b = (data && typeof data === 'object' ? data : {}) as { error?: unknown; message?: unknown; field?: unknown };
  const code = typeof b.error === 'string' && KNOWN.has(b.error) ? (b.error as ErrorCode) : codeForStatus(res.status);
  return new ApiError(res.status, code, typeof b.message === 'string' ? b.message : undefined,
    typeof b.field === 'string' ? b.field : undefined, data);
}

/* GET a file (the data export) with the same session and error handling */
export async function download(path: string, fallbackName: string): Promise<{ blob: Blob; name: string }> {
  let res: Response;
  try {
    res = await fetchImpl(path, { method: 'GET', credentials: 'same-origin', cache: 'no-store', headers: { accept: 'application/json' } });
  } catch { throw new ApiError(0, 'network'); }
  if (!res.ok) throw await toError(res);
  return { blob: await res.blob(), name: filenameFrom(res.headers.get('content-disposition')) ?? fallbackName };
}

/* the file name from a Content-Disposition header, made safe to save */
export function filenameFrom(header: string | null): string | null {
  if (!header) return null;
  let name: string | null = null;
  const star = /filename\*\s*=\s*(?:[\w-]+'[\w-]*')?("?)([^";]+)\1/i.exec(header);
  if (star) { try { name = decodeURIComponent(star[2]); } catch { name = star[2]; } }
  if (!name) name = /filename\s*=\s*"([^"]+)"/i.exec(header)?.[1] ?? /filename\s*=\s*([^;\s]+)/i.exec(header)?.[1] ?? null;
  if (!name) return null;
  name = name.replace(/[\\/:*?"<>|\u0000-\u001f]/g, '_').replace(/^\.+/, '').trim();
  return name || null;
}

export const api = {
  get: <T>(path: string, opts?: { signal?: AbortSignal }) => request<T>('GET', path, undefined, opts),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
};

/* What the page should do with an error: go somewhere, or show a message (an i18n key). */
export type ErrorAction =
  | { kind: 'redirect'; to: string; code: ClientErrorCode }
  | { kind: 'message'; key: MessageKey; code: ClientErrorCode; field?: string; serverMessage?: string };

const MESSAGES: Partial<Record<ClientErrorCode, MessageKey>> = {
  bad_credentials: 'err.bad_credentials',
  email_not_confirmed: 'err.email_not_confirmed',
  bad_code: 'err.bad_code',
  age_blocked: 'start.blocked.title',
  already_paid: 'err.already_paid',
  withdrawal_ack_required: 'err.withdrawal_ack_required',
  version_conflict: 'err.version_conflict',
  invalid: 'err.invalid',
  not_found: 'err.not_found',
  rate_limited: 'err.rate_limited',
  forbidden: 'err.forbidden',
  network: 'err.network',
  server_error: 'err.server_error',
  bad_signature: 'err.server_error',
  /* only reached when a page opts out of the redirect */
  signed_out: 'err.signed_out',
  mfa_required: 'err.mfa_required',
  guardian_consent_required: 'err.guardian_consent_required',
  plan_required: 'err.plan_required',
};

/* here = the current path and query (used as ?next=). noRedirect lists codes this page handles itself. */
export function errorAction(err: unknown, here: string, noRedirect: ReadonlyArray<ClientErrorCode> = []): ErrorAction {
  if (!(err instanceof ApiError)) return { kind: 'message', key: 'err.server_error', code: 'server_error' };
  const { code } = err;
  if (!noRedirect.includes(code)) {
    if (code === 'signed_out') return { kind: 'redirect', to: signinUrl(here), code };
    if (code === 'mfa_required') return { kind: 'redirect', to: codeUrl(here), code };
    if (code === 'guardian_consent_required') return { kind: 'redirect', to: WEB_ROUTES.guardianNeeded, code };
    if (code === 'plan_required') return { kind: 'redirect', to: WEB_ROUTES.plans, code };
  }
  return { kind: 'message', key: MESSAGES[code] ?? 'err.server_error', code, field: err.field, serverMessage: err.serverMessage };
}
